<template>
  <div v-if="post && post.data && post.id">
    <v-container fluid class="pa-0">
      <div class="p-relative">
        <v-img
          :src="post.data.main_image.url"
          :alt="`${post.data.title[0].text} main image`"
          :height="mainImageHeight"
          class="mb-8"
        ></v-img>
        <v-hover>
          <div
            slot-scope="{ hover }"
            class="top-actions-mask"
            @click="goBack()"
          >
            <v-btn :input-value="hover" class="my-2 ml-4" absolute fab small>
              <v-icon dark small>fal fa-arrow-left</v-icon>
            </v-btn>
          </div>
        </v-hover>
        <div class="title-overlay secondary--text hidden-sm-and-down">
          <div class="p-relative">
            <div class="gradient-bg">
              <v-container>
                <v-row justify="center">
                  <v-col cols="12" sm="10" lg="8" xl="6">
                    <div class="pb-8">
                      <h1 class="text-h3 font-weight-bold text-shadow">
                        {{ post.data.title[0].text }}
                      </h1>
                      <span class="text-subtitle-1 mb-6 text-shadow">
                        by
                        <span
                          class="font-weight-bold secondary--text text-shadow"
                          >{{
                            generateAuthor(post.data.post_author.slug)
                          }}</span
                        >
                        in
                        <span
                          class="secondary--text text-shadow text-underline"
                          @click="changeCategory(post.data.post_category)"
                          >{{ post.data.post_category.slug }}</span
                        >
                        - {{ post.data.date | timeago }}
                      </span>
                    </div>
                  </v-col>
                </v-row>
              </v-container>
            </div>
          </div>
        </div>
      </div>
    </v-container>
    <v-container>
      <v-row justify="center">
        <v-col cols="12" sm="10" lg="8" xl="6">
          <article v-if="post">
            <div class="meta hidden-md-and-up mb-6">
              <h1 class="text-h4 font-weight-bold">
                {{ post.data.title[0].text }}
              </h1>
              <span class="text-body-2 mb-6">
                by
                <nuxt-link to="/">{{
                  generateAuthor(post.data.post_author.slug)
                }}</nuxt-link>
                in
                <nuxt-link
                  :to="`/blog?category=${post.data.post_category.slug}&back=yes`"
                  >{{ post.data.post_category.slug }}</nuxt-link
                >
                - {{ post.data.date | timeago }}
              </span>
            </div>
            <v-breadcrumbs
              :items="breadcrumbsItems"
              divider=">"
              class="pa-0 mt-n6 mb-6"
            />
            <section v-for="(slice, index) in post.data.body" :key="index">
              <template v-if="slice.slice_type === 'text'">
                <prismic-rich-text :field="slice.primary.text" />
              </template>
              <template v-if="slice.slice_type === 'quote'">
                <prismic-rich-text
                  :field="slice.primary.quote"
                  :html-serializer="postQuoteSerializer"
                />
              </template>
              <template v-if="slice.slice_type === 'image_with_caption'">
                <div class="text-center mt-8 mb-6">
                  <v-img
                    :src="slice.primary.image.url"
                    :alt="`${post.data.title[0].text} - 100ASA`"
                  ></v-img>
                </div>
              </template>
              <template v-if="slice.slice_type === 'embed'">
                <div class="rwd-video mt-8 mb-6">
                  <iframe
                    :src="getYoutubeEmbedLink(slice.primary.embed.embed_url)"
                    width="560"
                    height="315"
                    frameborder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowfullscreen
                  ></iframe>
                </div>
              </template>
            </section>
            <v-divider class="my-8" />
            <share-post :uid="post.uid" />
            <div v-if="showDisqus" class="mt-6">
              <Disqus :page-config="disqusConfig" />
            </div>
          </article>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import moment from 'moment'
import prismicDOM from 'prismic-dom'
import { mapState } from 'vuex'
import SharePost from '@/components/blog/SharePost.vue'

const Elements = prismicDOM.RichText.Elements
const postQuoteSerializer = function (type, element, content, children) {
  if (type === Elements.paragraph) {
    return `<p class="subtitle-1 font-italic border-quote pl-4">${children.join(
      ''
    )}</p>`
  }
  return null
}

export default {
  filters: {
    timeago: date => {
      return moment.utc(date).local().fromNow()
    }
  },
  components: {
    SharePost
  },
  data() {
    return {
      postQuoteSerializer,
      showDisqus: false,
      disqusConfig: null
    }
  },
  async fetch({ store, params, redirect }) {
    try {
      await store.dispatch('blog/getPost', params.uid)
    } catch (error) {
      redirect('/blog')
    }
  },
  head({ $seo }) {
    return $seo({
      title: this.post.data.seo_title[0].text,
      description: this.post.data.seo_description[0].text,
      url: `https://100asa.com/blog/${this.post.uid}/`,
      image: {
        url: this.post.data.main_image.url,
        width: this.post.data.main_image.dimensions.width,
        height: this.post.data.main_image.dimensions.height
      },
      openGraph: {
        type: 'article'
      },
      twitter: {
        card: 'summary'
      }
    })
  },
  computed: {
    ...mapState({
      post: state => state.blog.post,
      loading: state => state.blog.loading
    }),
    breadcrumbsItems() {
      if (!this.post) {
        return []
      }
      return [
        {
          text: 'Home',
          disabled: false,
          nuxt: true,
          exact: true,
          to: '/'
        },
        {
          text: 'Blog',
          disabled: false,
          nuxt: true,
          exact: true,
          to: '/blog'
        },
        {
          text: this.post.data.title[0].text,
          disabled: true
        }
      ]
    },
    mainImageHeight() {
      switch (this.$vuetify.breakpoint.name) {
        case 'md':
        case 'sm':
          return 600
        case 'xs':
          return 400
        case 'xl':
        case 'lg':
        default:
          return 800
      }
    }
  },
  mounted() {
    if (this.post && this.post.uid) {
      this.disqusConfig = {
        url: `https://100asa.com${this.$route.path}`,
        identifier: this.$route.path,
        title: this.post.data.title[0].text
      }
      this.showDisqus = true
    }
  },
  methods: {
    getYoutubeEmbedLink(link) {
      const id = link.substring(link.lastIndexOf('/') + 1)
      return `https://www.youtube-nocookie.com/embed/${id}`
    },
    changeCategory(category) {
      this.$store.dispatch('blog/changeCategory', category)
      this.$router.push('/blog', {
        query: { category: category.slug }
      })
    },
    generateAuthor(slug) {
      if (slug === '100asa') {
        return '100ASA'
      }
      const split = slug.split('-')
      const name = this.$asa.capitalize(split[0])
      const surname = this.$asa.capitalize(split[1])
      return `${name} ${surname}`
    },
    goBack() {
      this.$router.push('/blog')
    }
  }
}
</script>

<style scoped>
.p-relative {
  position: relative;
}
.title-overlay {
  position: absolute;
  bottom: 0;
  width: 100%;
}
.gradient-bg {
  background: linear-gradient(
    to bottom,
    transparent 0%,
    rgba(0, 0, 0, 0.6) 100%
  );
  padding-top: 200px;
  position: absolute;
  bottom: 0;
  width: 100%;
}
.text-shadow {
  text-shadow: 0px 0px 1px rgba(0, 0, 0, 0.4);
}
.text-underline {
  cursor: pointer;
  text-decoration: underline;
}
.top-actions-mask {
  align-items: center;
  cursor: pointer;
  display: flex;
  position: absolute;
  top: 0;
  left: 0;
  width: 50%;
  height: 60px;
}
.rwd-video {
  height: 0;
  overflow: hidden;
  padding-bottom: 56.25%;
  padding-top: 30px;
  position: relative;
}
.rwd-video iframe,
.rwd-video object,
.rwd-video embed {
  height: 100%;
  left: 0;
  position: absolute;
  top: 0;
  width: 100%;
}
</style>

<style>
.border-quote {
  border-left: 3px solid rgba(0, 0, 0, 0.12);
}
</style>
